import { FieldError, InfoPopover, TextField } from '@automata/ui';
import FormControl from '@mui/material/FormControl';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import type { WorkflowSerializationError } from '../../../../features/workflowJsonSelectors';
import type { AnnotatedError } from '../../../../text/errorsToUserCopy';

/** Returns true if a workflow-level error should be shown in a SlotField. For filtering. */
export const isSlotFieldError = (err: WorkflowSerializationError): boolean => {
  switch (err.message) {
    case 'LABWARE_START_LOCATION_SELF_COLLISION':
    case 'LABWARE_START_LOCATION_SLOT_COLLISION':
    case 'LABWARE_START_LOCATION_SLOT_CAPACITY_EXCEEDED':
    case 'LABWARE_START_LOCATION_SLOT_INVALID':

    case 'LABWARE_FINAL_LOCATION_SELF_COLLISION':
    case 'LABWARE_FINAL_LOCATION_SLOT_COLLISION':
    case 'LABWARE_FINAL_LOCATION_SLOT_CAPACITY_EXCEEDED':

    case 'LABWARE_SLOT_COLLISION':
    case 'TASK_SLOT_FILLING_RULE_INVALID_SLOT_STRING':
    case 'LABWARE_SLOT_RANGE_GLOBAL_SIZE_MISMATCH':
    case 'CONSECUTIVE_TASK_SLOT_MISMATCH': {
      return true;
    }

    default: {
      return false;
    }
  }
};

const digitsRegex = /^\d+$/;
const digitsHyphenCommaSpaceRegex = /^[\d,\s-]+$/;
const multipleSpaceRegex = /\s\s+/g;
const multipleHyphenRegex = /--+/g;
const multipleCommaRegex = /,,+/g;

const slotRangePopover = (
  <InfoPopover
    title="Slot range"
    copy="Enter specific slots or ranges within the chosen instrument, separated by commas. Be aware: choosing only one slot may limit the number of batches to one."
    example="1, 5, 11, 21-30"
  />
);

const slotSinglePopover = (
  <InfoPopover
    title="Slot"
    copy="Enter specific slot (single integer) within the chosen instrument. The tower shaker containing the specified slot will shake. If no slot is specified both tower shakers will shake."
    example="11"
  />
);

export type SlotFieldProps = {
  errors?: AnnotatedError<WorkflowSerializationError>[];
  helperText?: string;
  id: string;
  isRange?: boolean;
  showLabel?: boolean;
  value: string;
  onChange: (value: string) => void;
};

const SlotField = ({
  errors,
  helperText,
  id,
  isRange = true,
  showLabel = true,
  value,
  onChange,
}: SlotFieldProps) => {
  // Internal state to allow clearing the field while editing
  const [internalValue, setInternalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  // Update internal state from props only when not focused
  useEffect(() => {
    if (!isFocused && value !== internalValue) {
      setInternalValue(value);
    }
  }, [value, isFocused, internalValue]);

  const errorMessages = useMemo(() => {
    const msgs =
      errors
        ?.filter((err) => isSlotFieldError(err.error))
        .map((err) => err.inputError ?? err.description) ?? [];
    return helperText ? [helperText, ...msgs] : msgs;
  }, [errors, helperText]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const candidateValue = e.target.value;
      const isValidInput =
        candidateValue === '' ||
        (isRange ? digitsHyphenCommaSpaceRegex : digitsRegex).test(
          candidateValue
        );

      if (!isValidInput) return;

      const cleanValue = candidateValue
        .replace(multipleSpaceRegex, ' ')
        .replace(multipleHyphenRegex, '-')
        .replace(multipleCommaRegex, ',');

      setInternalValue(cleanValue);
      onChange(cleanValue);
    },
    [isRange, onChange]
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const firstError = errorMessages[0];

  const computedLabel = showLabel
    ? isRange
      ? 'Slot range'
      : 'Slot'
    : undefined;

  const computedInfoPopover = useMemo(() => {
    if (!showLabel) return undefined;
    return isRange ? slotRangePopover : slotSinglePopover;
  }, [showLabel, isRange]);

  const infoAnchorOrigin = showLabel
    ? { vertical: 'top' as const, horizontal: 'right' as const }
    : undefined;

  const infoTransformOrigin = showLabel
    ? { vertical: 'bottom' as const, horizontal: 'right' as const }
    : undefined;

  return (
    <FormControl fullWidth>
      <TextField
        id={id}
        value={internalValue}
        type="text"
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        label={computedLabel}
        error={!!firstError}
        infoPopover={computedInfoPopover}
        infoPopoverAnchorOrigin={infoAnchorOrigin}
        infoPopoverTransformOrigin={infoTransformOrigin}
      />
      <FieldError error={errorMessages} />
    </FormControl>
  );
};

export default SlotField;
